import { isEmpty } from 'lodash'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import { Tooltip } from 'components/Tooltip'
import { isTicketCancelled } from 'resolvers/bookings'
import { getInsuranceTotalByTicket } from 'resolvers/insurance'
import { sanitizePrice } from 'utils/math'
import { getTotalServiceFeeByTicket } from 'resolvers/serviceFee'
import { HelpOutlineIcon } from 'components/Icons/HelpOutlineIcon'
import { getTotalLuggageAmountByTicket, getTotalPreferredSeatsAmountByTicket } from 'resolvers/ancillaries'
import { GTBookingV2, GTBookingV2Trip } from 'gql/types.generated'
import { ReactElement } from 'react'

const Table = styled.table<TSFixMe>`
  width: 100%;
  border-collapse: collapse;
`

const Tr = styled.tr<TSFixMe>`
  border-bottom: 1px solid #d8d8d8;

  &:last-child {
    border-bottom: none;
  }
`

const Td = styled.td<TSFixMe>`
  font-size: 16px;
  color: #1b1b3d;
  padding: ${(props): TSFixMe => (props.isFirst ? '15px 10px 15px 0' : '15px 10px')};
  vertical-align: top;
`

const PriceDifference = styled.div<TSFixMe>`
  ${(props): TSFixMe => {
    if (props.diff > 0) {
      return `
        color: #0076e0;
      `
    } else if (props.diff < 0) {
      return `
        color: #419845;
      `
    }
  }}
`
const AddonsAmountsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`

interface ITicketRowProps {
  booking: GTBookingV2
  editPriceDisabled?: boolean
  newTicket: TSFixMe
  oldTicket: TSFixMe
  hasNewTrip?: boolean
}
const TicketRow = ({ newTicket, oldTicket, hasNewTrip, editPriceDisabled, booking }: ITicketRowProps): ReactElement => {
  const { passenger } = newTicket

  const oldTotalAmountInsurance = getInsuranceTotalByTicket(oldTicket)
  const oldTotalAmountServiceFee = getTotalServiceFeeByTicket(oldTicket)
  const oldTotalAmountLuggage = getTotalLuggageAmountByTicket(oldTicket)
  const oldTotalAmountPreferredSeats = getTotalPreferredSeatsAmountByTicket(oldTicket)

  const oldTotalAmountAddons = oldTotalAmountServiceFee
  const oldTotalAmountAncillaries = oldTotalAmountInsurance + oldTotalAmountLuggage + oldTotalAmountPreferredSeats
  const oldTotal = oldTicket.subtotal + oldTicket.vat - oldTotalAmountAddons - oldTotalAmountAncillaries

  const newTotalAmountInsurance = getInsuranceTotalByTicket(newTicket)
  const newTotalAmountServiceFee = getTotalServiceFeeByTicket(newTicket)
  const newTotalAmountLuggage = getTotalLuggageAmountByTicket(newTicket)
  const newTotalAmountPreferredSeats = getTotalPreferredSeatsAmountByTicket(newTicket)

  const newTotalAmountAddons = newTotalAmountServiceFee
  const newTotalAmountAncillaries = newTotalAmountInsurance + newTotalAmountLuggage + newTotalAmountPreferredSeats
  const newTotal = newTicket.subtotal + newTicket.vat - newTotalAmountAddons - newTotalAmountAncillaries

  const priceDifference = newTotal - newTicket.discount - (oldTotal - oldTicket.discount)
  const hasPriceDifference = priceDifference > 0
  const pricesCurrency = booking.currency.code === 'USD' ? 'USD' : undefined

  let shownTotalAmountInsurance, shownTotalAmountServiceFee, shownTotalAmountLuggage, shownTotalAmountPreferredSeats
  if (hasNewTrip) {
    shownTotalAmountInsurance = newTotalAmountInsurance
    shownTotalAmountServiceFee = newTotalAmountServiceFee
    shownTotalAmountLuggage = newTotalAmountLuggage
    shownTotalAmountPreferredSeats = newTotalAmountPreferredSeats
  } else {
    shownTotalAmountInsurance = oldTotalAmountInsurance
    shownTotalAmountServiceFee = oldTotalAmountServiceFee
    shownTotalAmountLuggage = oldTotalAmountLuggage
    shownTotalAmountPreferredSeats = oldTotalAmountPreferredSeats
  }
  const hasInsurance = shownTotalAmountInsurance > 0
  const hasServiceFee = shownTotalAmountServiceFee > 0
  const hasLuggage = shownTotalAmountLuggage > 0
  const hasPrefferedSeats = shownTotalAmountPreferredSeats > 0
  const hasAncillaries = hasInsurance || hasLuggage || hasPrefferedSeats

  return (
    <Tr>
      <Td isFirst>
        {passenger.firstName} {passenger.lastName}
      </Td>
      <Td>
        <FormattedPrice value={sanitizePrice(oldTotal)} currency={pricesCurrency} />
      </Td>
      <Td>
        {oldTicket.discount > 0 && (
          <>
            -<FormattedPrice value={sanitizePrice(oldTicket.discount)} currency={pricesCurrency} />
          </>
        )}
      </Td>
      {!editPriceDisabled && (
        <Td>
          {(hasNewTrip || hasPriceDifference) && (
            <FormattedPrice value={sanitizePrice(newTotal)} currency={pricesCurrency} />
          )}
        </Td>
      )}
      {!editPriceDisabled && (
        <Td>
          {(hasNewTrip || hasPriceDifference) && (
            <>
              {newTicket.discount > 0 ? '-' : ''}
              <FormattedPrice value={sanitizePrice(newTicket.discount)} currency={pricesCurrency} />
            </>
          )}
        </Td>
      )}
      {!editPriceDisabled && (
        <Td>
          {(hasNewTrip || hasPriceDifference) && (
            <PriceDifference diff={priceDifference}>
              {priceDifference < 0 ? (
                <>
                  -<FormattedPrice value={sanitizePrice(-priceDifference)} currency={pricesCurrency} />
                </>
              ) : (
                <FormattedPrice value={sanitizePrice(priceDifference)} currency={pricesCurrency} />
              )}
            </PriceDifference>
          )}
        </Td>
      )}
      <Td>
        {hasAncillaries && (
          <AddonsAmountsContainer>
            <FormattedPrice value={sanitizePrice(oldTotalAmountAncillaries)} currency={pricesCurrency} />
            <span>
              {'('}
              {hasInsurance ? (
                <FormattedPrice value={sanitizePrice(oldTotalAmountInsurance)} currency={pricesCurrency} />
              ) : (
                'N/A'
              )}
            </span>
            <span>{'+'}</span>
            <span>
              {hasLuggage ? (
                <FormattedPrice value={sanitizePrice(oldTotalAmountLuggage)} currency={pricesCurrency} />
              ) : (
                'N/A'
              )}
            </span>
            <span>{'+'}</span>
            <span>
              {hasPrefferedSeats ? (
                <FormattedPrice value={sanitizePrice(oldTotalAmountPreferredSeats)} currency={pricesCurrency} />
              ) : (
                'N/A'
              )}
              {')'}
            </span>
          </AddonsAmountsContainer>
        )}
      </Td>
      <Td>
        {hasServiceFee ? (
          <AddonsAmountsContainer>
            <FormattedPrice value={sanitizePrice(oldTotalAmountServiceFee)} currency={pricesCurrency} />
          </AddonsAmountsContainer>
        ) : null}
      </Td>
    </Tr>
  )
}

const Th = styled.th<TSFixMe>`
  text-align: left;
  padding: ${(props): TSFixMe => (props.isFirst ? '10px 10px 10px 0' : '10px')};
  font-size: 16px;
  font-weight: 500;
  color: #1b1b3d;
  border-bottom: 1px solid #666666;
`

const AddonsHeader = styled.div<TSFixMe>`
  display: flex;
  gap: 5px;
`

interface IBookingPaymentsByTicketsProps {
  booking: GTBookingV2
  newTickets: TSFixMe[]
  editPriceDisabled?: boolean
  returnTrips: GTBookingV2Trip[]
}

export const BookingPaymentsByTickets = ({
  booking,
  newTickets,
  editPriceDisabled,
  returnTrips
}: IBookingPaymentsByTicketsProps): ReactElement => {
  const oldTickets =
    booking.tickets
      .filter((ticket) => {
        return !isTicketCancelled(ticket)
      })
      .map((ticket): TSFixMe => {
        const returnTrip = returnTrips.find((trip: TSFixMe) => trip.id === ticket.trip?.id)
        const newTicket = newTickets.find(({ id }: TSFixMe) => id === ticket.id)
        const payments = booking.payments.filter((payment: TSFixMe) => {
          return payment.tickets.find(({ id }: TSFixMe): TSFixMe => id === ticket.id)
        })

        return {
          ...ticket,
          payments,
          isReturn: !isEmpty(returnTrip) || newTicket?.isReturn
        }
      }) || []
  const oldDepartureTickets = oldTickets.filter(({ isReturn }: TSFixMe) => !isReturn)
  const oldReturnTickets = oldTickets.filter(({ isReturn }: TSFixMe) => isReturn)
  const newDepartureTickets = newTickets.filter(({ isReturn }: TSFixMe) => !isReturn)
  const newReturnTickets = newTickets.filter(({ isReturn }: TSFixMe) => isReturn)

  return (
    <Table>
      <thead>
        <tr>
          <Th isFirst>
            <FormattedMessage id="dictionary.passengers" defaultMessage="Passengers" />
          </Th>
          <Th>
            <FormattedMessage id="bookings.edit.payments.originalPrice" defaultMessage="Original Ticket $" />
          </Th>
          <Th>
            <FormattedMessage
              id="bookings.edit.payments.originalDiscount"
              defaultMessage="Original Ticket Discount $"
            />
          </Th>
          {!editPriceDisabled && (
            <Th>
              <FormattedMessage id="bookings.edit.payments.newPrice" defaultMessage="New Ticket $" />
            </Th>
          )}
          {!editPriceDisabled && (
            <Th>
              <FormattedMessage id="bookings.edit.payments.newDiscount" defaultMessage="New Ticket Discount $" />
            </Th>
          )}
          {!editPriceDisabled && (
            <Th>
              <FormattedMessage id="bookings.edit.payments.priceDifference" defaultMessage="Price Diff Tickets" />
            </Th>
          )}
          <Th>
            <AddonsHeader>
              <FormattedMessage id="bookings.edit.payments.addons" defaultMessage="Ancillaries" />
              <Tooltip toggle={<HelpOutlineIcon />} right>
                <FormattedMessage
                  id="bookings.edit.payments.tooltip.description"
                  defaultMessage="Travel Assistance + Luggage + Preferred Seats"
                />
              </Tooltip>
            </AddonsHeader>
          </Th>
          <Th>
            <AddonsHeader>
              <FormattedMessage id="bookings.edit.payments.addons" defaultMessage="Addons Total" />
            </AddonsHeader>
          </Th>
        </tr>
      </thead>
      <tbody>
        {newDepartureTickets.length > 0 && (
          <>
            {newReturnTickets.length > 0 && (
              <Tr>
                <Td colSpan="8" isFirst>
                  <FormattedMessage id="dictionary.departure" defaultMessage="Departure" />
                </Td>
              </Tr>
            )}
            {newDepartureTickets.map((ticket: TSFixMe, index: TSFixMe): TSFixMe => {
              const allOldTrips = [...booking.departureTrips, ...booking.returnTrips]
              const hasNewTrip = !allOldTrips.some(({ id }): TSFixMe => id === ticket.trip?.id)
              const oldTicket = oldDepartureTickets.find(({ id }: TSFixMe) => id === ticket.id)

              return (
                <TicketRow
                  key={index}
                  newTicket={ticket}
                  hasNewTrip={hasNewTrip}
                  oldTicket={oldTicket}
                  editPriceDisabled={editPriceDisabled}
                  booking={booking}
                />
              )
            })}
          </>
        )}
        {newReturnTickets.length > 0 && (
          <>
            {newDepartureTickets.length > 0 && (
              <>
                <tr>
                  <Td />
                </tr>
                <Tr>
                  <Td colSpan="8" isFirst>
                    <FormattedMessage id="dictionary.return" defaultMessage="Return" />
                  </Td>
                </Tr>
              </>
            )}
            {newReturnTickets.map((ticket: TSFixMe, index: TSFixMe): TSFixMe => {
              const allOldTrips = [...booking.departureTrips, ...booking.returnTrips]
              const hasNewTrip = !allOldTrips.some(({ id }): TSFixMe => id === ticket.trip?.id)
              const oldTicket = oldReturnTickets.find(({ id }: TSFixMe) => id === ticket.id)

              return (
                <TicketRow
                  key={index}
                  newTicket={ticket}
                  hasNewTrip={hasNewTrip}
                  oldTicket={oldTicket}
                  editPriceDisabled={editPriceDisabled}
                  booking={booking}
                />
              )
            })}
          </>
        )}
      </tbody>
    </Table>
  )
}
